/**
 * S5 — Salience Monitor home (the "terminal"). Answers the plan §5 questions in
 * a markets-dashboard register, since Gary trades by background:
 *   • watchlist tiles — every tracked term's headline attention + velocity
 *   • the centerpiece trend chart — one term, per agenda, with Gary's uploads
 *     AND exogenous events marked, spikes flagged (questions 1 + 4)
 *   • spike alert feed + candidate influence findings (question 4)
 *
 * Honest labelling throughout (plan §1.1): we measure ATTENTION / agenda-
 * prominence, never "importance"; the four agendas are never blended.
 *
 * Per-term deep dives (geo, stance/frame, window-position) live on the term
 * detail page.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { api, apiSalience } from "../lib/api";
import type {
  SalienceInfluence,
  SalienceMetricPoint,
  SalienceOverviewTerm,
  SalienceSpike,
} from "../lib/api";
import { MetricTile } from "../components/salience/MetricTile";
import { ShareTrendChart } from "../components/salience/ShareTrendChart";
import { SpikeFeed } from "../components/salience/SpikeFeed";
import { InfluenceCandidates } from "../components/salience/InfluenceCandidates";
import { Panel } from "../components/salience/Panel";
import {
  WhatIsThisCallout,
  FourAgendasCallout,
  TrackedTermsCallout,
  AttentionNotImportanceCallout,
  InfluenceCallout,
  PrototypeNote,
} from "../components/salience/explainers";
import { metricValue, termHeadline } from "../components/salience/format";

const KIND_ORDER: Record<string, number> = { topic: 0, tagline: 1, entity: 2 };

// The watchlist is long and most terms are quiet, so we surface a curated
// priority row by default and roll the rest up behind a toggle. These four are
// the demo's headline terms, shown in this order regardless of live value.
// 2026-07-04: swapped patriotic_millionaires (→ rolled up under "show more")
// for how_to_get_filthy_rich, the Channel 4 documentary (broadcast 8 Jul) —
// the current mainstream-cut-through story, tracked like the_trading_game book.
const PRIORITY_TERM_IDS = [
  "wealth_tax",
  "gary_stevenson",
  "how_to_get_filthy_rich",
  "tax_wealth_not_work",
];

export function SalienceOverview() {
  const overviewQ = useQuery({
    queryKey: ["salience", "overview"],
    queryFn: apiSalience.overview,
    retry: (n, err) =>
      !(err instanceof Error && err.message.includes("404")) && n < 1,
  });
  // Sparklines only need recent history — post-S7 the unscoped full fetch is
  // ~150k points, so window it to the last 90 days. The featured chart gets
  // its own term-scoped FULL-history fetch below.
  const sparkFrom = useMemo(
    () => new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10),
    [],
  );
  const seriesQ = useQuery({
    queryKey: ["salience", "timeseries", "recent", sparkFrom],
    queryFn: () => apiSalience.timeseries({ from: sparkFrom }),
  });
  const eventsQ = useQuery({
    queryKey: ["salience", "events", "all"],
    queryFn: () => apiSalience.events(),
  });
  const calendarQ = useQuery({
    queryKey: ["salience", "calendar"],
    queryFn: () => apiSalience.calendar(),
  });
  const videosQ = useQuery({ queryKey: ["videos"], queryFn: api.videos });
  // The full active tracked-term registry — so the watchlist shows EVERY
  // monitored term, not just the ones with a scored metric series yet (most
  // adjacent creators/economists are quiet until they co-occur with coverage).
  const termsQ = useQuery({
    queryKey: ["salience", "terms"],
    queryFn: () => apiSalience.terms(),
  });

  const terms = overviewQ.data?.terms ?? {};
  // Term ids that actually have a metric series (the featured chart needs data).
  const sortedTermIds = useMemo(() => {
    return Object.keys(terms).sort((a, b) => {
      const ka = KIND_ORDER[terms[a].kind] ?? 9;
      const kb = KIND_ORDER[terms[b].kind] ?? 9;
      if (ka !== kb) return ka - kb;
      const ha = termHeadline(terms[a])?.value ?? -1;
      const hb = termHeadline(terms[b])?.value ?? -1;
      return hb - ha;
    });
  }, [terms]);

  const [featured, setFeatured] = useState<string | null>(null);
  const featuredId =
    featured ??
    (terms["wealth_tax"]
      ? "wealth_tax"
      : sortedTermIds[0] ?? null);

  // Full history for the ONE term on the featured chart (windowing/zoom in the
  // chart is client-side over this).
  const featuredSeriesQ = useQuery({
    queryKey: ["salience", "timeseries", "term", featuredId],
    queryFn: () => apiSalience.timeseries({ term: featuredId! }),
    enabled: !!featuredId,
  });

  // Group the single all-series fetch by term for sparklines + the chart.
  const pointsByTerm = useMemo(() => {
    const map: Record<string, SalienceMetricPoint[]> = {};
    for (const p of seriesQ.data ?? []) (map[p.termId] ??= []).push(p);
    return map;
  }, [seriesQ.data]);

  // Watchlist over the FULL active registry: every term gets a tile, with a
  // headline where it has metrics and a faded "no signal yet" state where it
  // doesn't. Terms with data sort first (by kind, then value); quiet terms after
  // (by kind, then label).
  const watchlist = useMemo(() => {
    const rows = (termsQ.data ?? []).map((t) => {
      const ov = terms[t.term_id];
      const headline = ov ? termHeadline(ov) : null;
      const spark = ov ? sparkFor(ov, pointsByTerm[t.term_id] ?? []) : [];
      return { termId: t.term_id, label: t.label, kind: t.kind, headline, spark };
    });
    return rows.sort((a, b) => {
      if (!!a.headline !== !!b.headline) return a.headline ? -1 : 1;
      const ka = KIND_ORDER[a.kind] ?? 9;
      const kb = KIND_ORDER[b.kind] ?? 9;
      if (ka !== kb) return ka - kb;
      if (a.headline && b.headline) return (b.headline.value ?? -1) - (a.headline.value ?? -1);
      return a.label.localeCompare(b.label);
    });
  }, [termsQ.data, terms, pointsByTerm]);

  const liveCount = watchlist.filter((w) => w.headline).length;

  // Priority terms first (in PRIORITY_TERM_IDS order), then the rest as sorted.
  const orderedWatchlist = useMemo(() => {
    const priorityRank = new Map(PRIORITY_TERM_IDS.map((id, i) => [id, i]));
    const priority = watchlist
      .filter((w) => priorityRank.has(w.termId))
      .sort(
        (a, b) => priorityRank.get(a.termId)! - priorityRank.get(b.termId)!,
      );
    const rest = watchlist.filter((w) => !priorityRank.has(w.termId));
    return [...priority, ...rest];
  }, [watchlist]);

  const [showAllTerms, setShowAllTerms] = useState(false);
  const visibleWatchlist = showAllTerms
    ? orderedWatchlist
    : orderedWatchlist.slice(0, PRIORITY_TERM_IDS.length);
  const hiddenCount = orderedWatchlist.length - visibleWatchlist.length;

  const spikesByTerm = useMemo(() => {
    const map: Record<string, SalienceSpike[]> = {};
    for (const e of eventsQ.data ?? [])
      if (e.kind === "spike") (map[e.termId] ??= []).push(e);
    return map;
  }, [eventsQ.data]);

  const allSpikes = (eventsQ.data ?? []).filter(
    (e): e is SalienceSpike => e.kind === "spike",
  );
  const allInfluence = (eventsQ.data ?? []).filter(
    (e): e is SalienceInfluence => e.kind === "influence",
  );
  const termLabels = useMemo(() => {
    const m: Record<string, string> = {};
    for (const [id, t] of Object.entries(terms)) m[id] = t.label;
    return m;
  }, [terms]);

  if (overviewQ.isLoading) return <SalienceSkeleton />;

  if (overviewQ.error) {
    const is404 =
      overviewQ.error instanceof Error &&
      overviewQ.error.message.includes("404");
    return (
      <Shell>
        <Panel title={is404 ? "No salience snapshot yet" : "Couldn't load salience"}>
          <p className="text-sm text-terminal-ink-muted">
            {is404
              ? "The daily score pass (09:30 UTC) computes the overview from coded observations. It'll be here on the next run."
              : "The /api/salience/overview call failed. Check the salience function logs and try again."}
          </p>
        </Panel>
      </Shell>
    );
  }

  const featuredTerm: SalienceOverviewTerm | null = featuredId
    ? terms[featuredId]
    : null;

  return (
    <Shell computedAt={overviewQ.data?.computed_at}>
      {/* Watchlist tiles — full active registry */}
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Watchlist{" "}
          <span className="font-normal normal-case tracking-normal text-ink-muted/70">
            · {liveCount} of {watchlist.length} with a live signal
          </span>
        </h2>
        <p className="hidden max-w-md text-[11px] text-ink-muted/80 sm:block">
          Each tile headlines the conversation where the term has its strongest
          live signal: <span className="text-ink-muted">media</span> as a %
          coverage share, <span className="text-ink-muted">public</span> and{" "}
          <span className="text-ink-muted">elite</span> as a daily item count.
        </p>
      </div>
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {visibleWatchlist.map((w) => (
            <MetricTile
              key={w.termId}
              termId={w.termId}
              label={w.label}
              kind={w.kind}
              headline={w.headline}
              spark={w.spark}
            />
          ))}
        </div>
        {orderedWatchlist.length > PRIORITY_TERM_IDS.length && (
          <button
            type="button"
            onClick={() => setShowAllTerms((v) => !v)}
            aria-expanded={showAllTerms}
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink"
          >
            <ChevronDown
              size={14}
              aria-hidden
              className={`transition-transform ${showAllTerms ? "rotate-180" : ""}`}
            />
            {showAllTerms
              ? "Show fewer"
              : `Show ${hiddenCount} more tracked terms`}
          </button>
        )}
        {showAllTerms && liveCount < watchlist.length && (
          <p className="mt-3 text-[11px] text-ink-muted/70">
            Faded tiles are tracked terms with no scored data yet. They light up
            as soon as their sources produce something.
          </p>
        )}
      </div>

      {/* Featured trend chart */}
      {featuredTerm && featuredId && (
        <div className="mb-6">
          <div className="mb-2 flex items-center gap-2 text-[11px] text-terminal-ink-muted">
            <span className="uppercase tracking-wider">Featured</span>
            <select
              value={featuredId}
              onChange={(e) => setFeatured(e.target.value)}
              className="rounded border border-white/15 bg-terminal-panel px-2 py-1 text-xs text-terminal-ink focus:border-white/40 focus:outline-none"
            >
              {sortedTermIds.map((id) => (
                <option key={id} value={id} className="bg-terminal-panel">
                  {terms[id].label}
                </option>
              ))}
            </select>
          </div>
          <ShareTrendChart
            termId={featuredId}
            termLabel={featuredTerm.label}
            points={featuredSeriesQ.data ?? pointsByTerm[featuredId] ?? []}
            spikes={spikesByTerm[featuredId] ?? []}
            calendar={calendarQ.data ?? []}
            uploads={videosQ.data ?? []}
            defaultAgenda={featuredTerm.kind === "topic" ? "media" : "public"}
          />
        </div>
      )}

      {/* Spike feed + influence candidates */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel
          title="What just spiked"
          eyebrow="Anomalies"
          hint="Sudden surges above the recent norm. Each is tagged with any nearby Gary upload (a sign of cut-through) and any big event on the same day (which might be the real cause)."
        >
          <SpikeFeed
            spikes={allSpikes}
            linkTerms
            termLabels={termLabels}
            emptyMessage="No spikes in the current window."
          />
        </Panel>

        <Panel
          title="Candidate influence"
          eyebrow="Lead-lag"
          hint="Where attention in one conversation tends to rise just before another. A hint one may be leading the other, never proof of cause."
          info={<InfluenceCallout tone="dark" />}
        >
          <InfluenceCandidates
            items={allInfluence}
            linkTerms
            termLabels={termLabels}
          />
        </Panel>
      </div>
    </Shell>
  );
}

/* -------------------------------------------------------------------------- */

/** Build a sparkline array from a term's headline agenda/geo series. */
function sparkFor(
  term: SalienceOverviewTerm,
  points: SalienceMetricPoint[],
): number[] {
  const h = termHeadline(term);
  if (!h) return [];
  return points
    .filter((p) => p.agenda === h.agenda && p.geo === h.geo)
    .sort((a, b) => a.bucketDate.localeCompare(b.bucketDate))
    .map((p) => metricValue(p, h.agenda))
    .filter((v): v is number => v != null);
}

function Shell({
  computedAt,
  children,
}: {
  computedAt?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <header className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
          Salience monitor · Agent 2
        </div>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink">
          Wealth-tax attention &amp; Gary's cut-through
        </h1>
        <p className="mt-1.5 max-w-2xl text-sm text-ink-muted">
          How prominent the wealth-tax conversation is across four agendas
          (public, search, media and elite), and where Gary's framing traces into
          it. The watchlist also follows related topics and the people and
          organisations around the movement. These are{" "}
          <span className="font-medium text-ink">attention</span> signals
          (prominence over time), <span className="italic">not</span> a measure
          of how much people care.
          {computedAt && (
            <span className="ml-1 text-ink-muted/80">
              Scored {relative(computedAt)}.
            </span>
          )}
        </p>
        <p className="mt-2 max-w-2xl text-xs text-ink-muted">
          Built on peer-reviewed methods (agenda-setting, stance analysis,
          lead-lag).{" "}
          <Link
            to="/salience/methods"
            className="font-medium text-brand-indigo hover:underline"
          >
            Find out more →
          </Link>
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <WhatIsThisCallout tone="light" />
          <TrackedTermsCallout tone="light" />
          <AttentionNotImportanceCallout tone="light" />
        </div>
        <div className="mt-2.5 max-w-2xl rounded-lg border border-rule bg-black/2 px-3 py-2">
          <PrototypeNote tone="light" variant="inline" />
        </div>
      </header>
      {children}
      <footer className="mt-8 border-t border-rule pt-4 text-[11px] leading-relaxed text-ink-muted/80">
        <p className="max-w-2xl">
          Four conversations, measured separately and never blended into one
          number: public as a count of relevant YouTube videos, search as a
          Google Trends index, media as a share of global news coverage, and elite
          as a count of relevant UK Parliament items.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <FourAgendasCallout tone="light" />
          <Link
            to="/salience/methods"
            className="text-[11px] font-medium text-brand-indigo hover:underline"
          >
            Methods &amp; sources →
          </Link>
          <Link
            to="/salience/eval"
            className="text-[11px] font-medium text-brand-indigo hover:underline"
          >
            Coder reliability →
          </Link>
        </div>
      </footer>
    </div>
  );
}

function SalienceSkeleton() {
  return (
    <div>
      <div className="mb-6">
        <div className="h-3 w-40 animate-pulse rounded bg-black/5" />
        <div className="mt-2 h-7 w-96 max-w-full animate-pulse rounded bg-black/5" />
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="h-28 animate-pulse rounded-xl bg-terminal-panel/50"
          />
        ))}
      </div>
      <div className="h-[420px] animate-pulse rounded-xl bg-terminal-panel/50" />
    </div>
  );
}

function relative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
